<template>
  <CompanyAppLayout 
    page-title="Knowledge Hub" 
    page-subtitle="Access training materials, safety guidelines, and industry resources"
  >
    <!-- Knowledge Hub Content -->
    <div class="space-y-8 pt-8">
      <!-- Search and Filter Section -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div class="flex flex-col md:flex-row gap-4">
          <div class="flex-1">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search knowledge base..." 
              class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>
          <div class="flex gap-2">
            <select 
              v-model="selectedCategory" 
              class="px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
              <option value="">All Categories</option>
              <option value="safety">Safety Guidelines</option>
              <option value="training">Training Materials</option>
              <option value="procedures">Procedures</option>
              <option value="regulations">Regulations</option>
            </select>
            <button 
              @click="searchKnowledge"
              class="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      <!-- Quick Access Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="card in quickAccessCards" 
          :key="card.id"
          class="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer group"
          @click="navigateToSection(card.route)"
        >
          <div class="flex items-center space-x-4">
            <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <component :is="card.icon" class="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                {{ card.title }}
              </h3>
              <p class="text-slate-400 text-sm">{{ card.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Articles -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-700/50">
          <h2 class="text-xl font-semibold text-white">Recent Articles</h2>
        </div>
        
        <div class="divide-y divide-slate-700/50">
          <div 
            v-for="article in recentArticles" 
            :key="article.id"
            class="p-6 hover:bg-slate-700/30 transition-colors cursor-pointer"
            @click="viewArticle(article)"
          >
            <div class="flex items-start space-x-4">
              <img :src="article.image" :alt="article.title" class="w-16 h-16 object-cover rounded-lg" />
              <div class="flex-1">
                <h3 class="text-lg font-medium text-white mb-2">{{ article.title }}</h3>
                <p class="text-slate-400 text-sm mb-3">{{ article.excerpt }}</p>
                <div class="flex items-center space-x-4 text-xs text-slate-500">
                  <span>{{ article.category }}</span>
                  <span>{{ formatDate(article.date) }}</span>
                  <span>{{ article.readTime }} min read</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Training Modules -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-700/50">
          <h2 class="text-xl font-semibold text-white">Training Modules</h2>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
          <div 
            v-for="module in trainingModules" 
            :key="module.id"
            class="bg-slate-700/30 border border-slate-600/50 rounded-lg p-4 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer group"
            @click="startTraining(module)"
          >
            <div class="flex items-center space-x-3 mb-3">
              <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <component :is="module.icon" class="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 class="text-lg font-medium text-white">{{ module.title }}</h3>
                <p class="text-slate-400 text-sm">{{ module.duration }}</p>
              </div>
            </div>
            <p class="text-slate-300 text-sm mb-3">{{ module.description }}</p>
            <div class="flex items-center justify-between">
              <span class="text-xs text-slate-500">{{ module.category }}</span>
              <span class="text-xs text-emerald-400">{{ module.progress }}% Complete</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Company Documents -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between">
          <h2 class="text-xl font-semibold text-white">Company Documents</h2>
          <button 
            @click="showUploadModal = true"
            class="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            <span>Upload Document</span>
          </button>
        </div>
        
        <!-- Document Filters -->
        <div class="px-6 py-4 border-b border-slate-700/50">
          <div class="flex flex-col md:flex-row gap-4">
            <div class="flex-1">
              <input 
                v-model="documentSearch" 
                type="text" 
                placeholder="Search documents..." 
                class="w-full px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              />
            </div>
            <select 
              v-model="documentCategory" 
              class="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
              <option value="">All Categories</option>
              <option value="safety">Safety Documents</option>
              <option value="procedures">Procedures</option>
              <option value="training">Training Materials</option>
              <option value="compliance">Compliance</option>
              <option value="equipment">Equipment Manuals</option>
              <option value="emergency">Emergency Procedures</option>
            </select>
            <select 
              v-model="documentType" 
              class="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
              <option value="">All Types</option>
              <option value="pdf">PDF</option>
              <option value="doc">Word Document</option>
              <option value="xls">Excel Spreadsheet</option>
              <option value="ppt">PowerPoint</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </div>
        </div>
        
        <!-- Documents List -->
        <div class="p-6">
          <div v-if="isLoadingDocuments" class="text-center py-8">
            <div class="w-8 h-8 bg-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p class="text-slate-400">Loading documents...</p>
          </div>
          
          <div v-else-if="filteredDocuments.length === 0" class="text-center py-8">
            <div class="w-16 h-16 bg-slate-700/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-white mb-2">No documents found</h3>
            <p class="text-slate-400">Upload your first document to share with contractors</p>
          </div>
          
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="document in filteredDocuments" 
              :key="document.id"
              class="bg-slate-700/30 border border-slate-600/50 rounded-lg p-4 hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div class="flex items-start space-x-3 mb-3">
                <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-medium text-white truncate">{{ document.title }}</h3>
                  <p class="text-slate-400 text-sm">{{ document.category }}</p>
                </div>
              </div>
              
              <p class="text-slate-300 text-sm mb-3 line-clamp-2">{{ document.description }}</p>
              
              <div class="flex items-center justify-between text-xs text-slate-500 mb-3">
                <span>{{ document.fileType.toUpperCase() }}</span>
                <span>{{ formatFileSize(document.fileSize) }}</span>
              </div>
              
              <div class="flex items-center justify-between text-xs text-slate-500 mb-3">
                <span>Uploaded {{ formatDate(document.uploadedAt) }}</span>
                <span>{{ document.downloads }} downloads</span>
              </div>
              
              <div class="flex items-center space-x-2">
                <button 
                  @click="downloadDocument(document)"
                  class="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <span>Download</span>
                </button>
                <button 
                  @click="previewDocument(document)"
                  class="flex items-center justify-center px-3 py-2 bg-slate-600/50 text-slate-300 rounded-lg hover:bg-slate-600/70 transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                </button>
                <button 
                  v-if="isCompanyUser"
                  @click="deleteDocument(document.id)"
                  class="flex items-center justify-center px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Safety Guidelines -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-700/50">
          <h2 class="text-xl font-semibold text-white">Safety Guidelines</h2>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div 
            v-for="guideline in safetyGuidelines" 
            :key="guideline.id"
            class="bg-slate-700/30 border border-slate-600/50 rounded-lg p-4 hover:border-red-500/50 transition-all duration-300 cursor-pointer group"
            @click="viewGuideline(guideline)"
          >
            <div class="flex items-center space-x-3 mb-3">
              <div class="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <component :is="guideline.icon" class="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 class="text-lg font-medium text-white">{{ guideline.title }}</h3>
                <p class="text-slate-400 text-sm">{{ guideline.type }}</p>
              </div>
            </div>
            <p class="text-slate-300 text-sm mb-3">{{ guideline.description }}</p>
            <div class="flex items-center justify-between">
              <span class="text-xs text-slate-500">Last Updated: {{ formatDate(guideline.updatedAt) }}</span>
              <span class="text-xs text-red-400">Required</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Document Upload Modal -->
      <div v-if="showUploadModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div class="bg-slate-800/95 border border-slate-700/50 rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-2xl font-bold text-white">Upload Document</h3>
            <button @click="showUploadModal = false" class="text-slate-400 hover:text-white transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <form @submit.prevent="uploadDocument" class="space-y-6">
            <!-- File Upload -->
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Select File</label>
              <div 
                @click="$refs.fileInput.click()"
                @dragover.prevent
                @drop.prevent="handleFileDrop"
                class="border-2 border-dashed border-slate-600/50 rounded-lg p-8 text-center hover:border-emerald-500/50 transition-colors cursor-pointer"
                :class="{ 'border-emerald-500/50 bg-emerald-500/10': selectedFile }"
              >
                <div v-if="!selectedFile">
                  <svg class="w-12 h-12 text-slate-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                  </svg>
                  <p class="text-slate-400 mb-2">Click to select or drag and drop</p>
                  <p class="text-slate-500 text-sm">PDF, DOC, XLS, PPT, Images, Videos (Max 50MB)</p>
                </div>
                <div v-else class="text-emerald-400">
                  <svg class="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <p class="font-medium">{{ selectedFile.name }}</p>
                  <p class="text-sm">{{ formatFileSize(selectedFile.size) }}</p>
                </div>
              </div>
              <input 
                ref="fileInput"
                type="file" 
                @change="handleFileSelect"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov"
                class="hidden"
              />
            </div>

            <!-- Document Details -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Document Title</label>
                <input 
                  v-model="uploadForm.title" 
                  type="text" 
                  required
                  placeholder="Enter document title"
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select 
                  v-model="uploadForm.category" 
                  required
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                >
                  <option value="">Select Category</option>
                  <option value="safety">Safety Documents</option>
                  <option value="procedures">Procedures</option>
                  <option value="training">Training Materials</option>
                  <option value="compliance">Compliance</option>
                  <option value="equipment">Equipment Manuals</option>
                  <option value="emergency">Emergency Procedures</option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea 
                v-model="uploadForm.description" 
                rows="3"
                placeholder="Brief description of the document"
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
              ></textarea>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Access Level</label>
                <select 
                  v-model="uploadForm.accessLevel" 
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                >
                  <option value="all">All Contractors</option>
                  <option value="specific">Specific Contractors</option>
                  <option value="managers">Managers Only</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Expiration Date (Optional)</label>
                <input 
                  v-model="uploadForm.expirationDate" 
                  type="date"
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center justify-end space-x-4 pt-4">
              <button 
                type="button"
                @click="showUploadModal = false"
                class="px-6 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700/50 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit"
                :disabled="!selectedFile || isUploading"
                class="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="isUploading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
                <span>{{ isUploading ? 'Uploading...' : 'Upload Document' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { computed, onMounted, ref } from 'vue'

const userStore = useUserStore()

// Reactive data
const searchQuery = ref('')
const selectedCategory = ref('')

// Document management
const showUploadModal = ref(false)
const isLoadingDocuments = ref(false)
const isUploading = ref(false)
const selectedFile = ref<File | null>(null)
const documentSearch = ref('')
const documentCategory = ref('')
const documentType = ref('')

// Upload form
const uploadForm = ref({
  title: '',
  category: '',
  description: '',
  accessLevel: 'all',
  expirationDate: ''
})

// Documents data
const documents = ref<any[]>([])

// Quick access cards data
const quickAccessCards = ref([
  {
    id: 1,
    title: 'Safety Protocols',
    description: 'Essential safety guidelines and procedures',
    icon: 'ShieldCheckIcon',
    route: '/company/safety'
  },
  {
    id: 2,
    title: 'Training Center',
    description: 'Comprehensive training materials and courses',
    icon: 'AcademicCapIcon',
    route: '/company/training'
  },
  {
    id: 3,
    title: 'Procedures',
    description: 'Standard operating procedures and workflows',
    icon: 'ClipboardDocumentListIcon',
    route: '/company/procedures'
  },
  {
    id: 4,
    title: 'Regulations',
    description: 'Industry regulations and compliance requirements',
    icon: 'DocumentTextIcon',
    route: '/company/regulations'
  },
  {
    id: 5,
    title: 'Best Practices',
    description: 'Industry best practices and guidelines',
    icon: 'StarIcon',
    route: '/company/best-practices'
  },
  {
    id: 6,
    title: 'Emergency Response',
    description: 'Emergency procedures and response protocols',
    icon: 'ExclamationTriangleIcon',
    route: '/company/emergency'
  }
])

// Recent articles data
const recentArticles = ref([
  {
    id: 1,
    title: 'Updated Safety Protocols for 2024',
    excerpt: 'New safety guidelines and procedures for oilfield operations...',
    category: 'Safety',
    date: '2024-01-15',
    readTime: 5,
    image: '/src/assets/images/knowledge/safety-protocols.jpg'
  },
  {
    id: 2,
    title: 'Tax Optimization Strategies for Companies',
    excerpt: 'Comprehensive guide to tax optimization and deduction strategies...',
    category: 'Tax',
    date: '2024-01-12',
    readTime: 8,
    image: '/src/assets/images/knowledge/tax-strategies.jpg'
  },
  {
    id: 3,
    title: 'Equipment Maintenance Best Practices',
    excerpt: 'Essential maintenance procedures for oilfield equipment...',
    category: 'Equipment',
    date: '2024-01-10',
    readTime: 6,
    image: '/src/assets/images/knowledge/equipment-maintenance.jpg'
  }
])

// Training modules data
const trainingModules = ref([
  {
    id: 1,
    title: 'Safety Training',
    description: 'Comprehensive safety training for all employees',
    duration: '2 hours',
    category: 'Safety',
    progress: 75,
    icon: 'ShieldCheckIcon'
  },
  {
    id: 2,
    title: 'Equipment Operation',
    description: 'Proper operation and maintenance of equipment',
    duration: '3 hours',
    category: 'Equipment',
    progress: 45,
    icon: 'WrenchScrewdriverIcon'
  },
  {
    id: 3,
    title: 'Emergency Response',
    description: 'Emergency procedures and response protocols',
    duration: '1.5 hours',
    category: 'Safety',
    progress: 90,
    icon: 'ExclamationTriangleIcon'
  }
])

// Safety guidelines data
const safetyGuidelines = ref([
  {
    id: 1,
    title: 'Personal Protective Equipment',
    description: 'Required PPE for different work environments',
    type: 'Equipment',
    updatedAt: '2024-01-15',
    icon: 'ShieldCheckIcon'
  },
  {
    id: 2,
    title: 'Hazard Communication',
    description: 'Proper communication of workplace hazards',
    type: 'Communication',
    updatedAt: '2024-01-12',
    icon: 'ChatBubbleLeftRightIcon'
  },
  {
    id: 3,
    title: 'Emergency Evacuation',
    description: 'Emergency evacuation procedures and routes',
    type: 'Emergency',
    updatedAt: '2024-01-10',
    icon: 'ExclamationTriangleIcon'
  },
  {
    id: 4,
    title: 'Equipment Safety',
    description: 'Safety procedures for equipment operation',
    type: 'Equipment',
    updatedAt: '2024-01-08',
    icon: 'WrenchScrewdriverIcon'
  }
])

// Computed properties
const isCompanyUser = computed(() => {
  return userStore.currentUser?.userType === 'company'
})

const filteredDocuments = computed(() => {
  return documents.value.filter(doc => {
    if (documentSearch.value && !doc.title.toLowerCase().includes(documentSearch.value.toLowerCase())) return false
    if (documentCategory.value && doc.category !== documentCategory.value) return false
    if (documentType.value && doc.fileType !== documentType.value) return false
    return true
  })
})

// Methods
const searchKnowledge = () => {
  console.log('Searching knowledge base:', { query: searchQuery.value, category: selectedCategory.value })
}

const loadDocuments = async () => {
  try {
    isLoadingDocuments.value = true
    const response = await fetch('/api/company/documents', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      documents.value = data.documents
    } else {
      // Load mock data for demo
      loadMockDocuments()
    }
  } catch (error) {
    console.error('Error loading documents:', error)
    loadMockDocuments()
  } finally {
    isLoadingDocuments.value = false
  }
}

const loadMockDocuments = () => {
  documents.value = [
    {
      id: 1,
      title: 'Safety Protocol Manual 2024',
      description: 'Comprehensive safety guidelines and procedures for all field operations',
      category: 'safety',
      fileType: 'pdf',
      fileSize: 2048576,
      uploadedAt: '2024-01-15T10:30:00Z',
      downloads: 45,
      accessLevel: 'all'
    },
    {
      id: 2,
      title: 'Equipment Maintenance Checklist',
      description: 'Detailed maintenance procedures and checklists for all equipment',
      category: 'equipment',
      fileType: 'xls',
      fileSize: 512000,
      uploadedAt: '2024-01-12T14:15:00Z',
      downloads: 32,
      accessLevel: 'all'
    },
    {
      id: 3,
      title: 'Emergency Response Procedures',
      description: 'Emergency protocols and response procedures for critical situations',
      category: 'emergency',
      fileType: 'pdf',
      fileSize: 1536000,
      uploadedAt: '2024-01-10T09:45:00Z',
      downloads: 28,
      accessLevel: 'all'
    }
  ]
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    selectedFile.value = target.files[0]
  }
}

const handleFileDrop = (event: DragEvent) => {
  event.preventDefault()
  if (event.dataTransfer && event.dataTransfer.files[0]) {
    selectedFile.value = event.dataTransfer.files[0]
  }
}

const uploadDocument = async () => {
  if (!selectedFile.value) return
  
  try {
    isUploading.value = true
    
    const formData = new FormData()
    formData.append('file', selectedFile.value)
    formData.append('title', uploadForm.value.title)
    formData.append('category', uploadForm.value.category)
    formData.append('description', uploadForm.value.description)
    formData.append('accessLevel', uploadForm.value.accessLevel)
    if (uploadForm.value.expirationDate) {
      formData.append('expirationDate', uploadForm.value.expirationDate)
    }
    
    const response = await fetch('/api/company/documents/upload', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: formData
    })
    
    if (response.ok) {
      const result = await response.json()
      documents.value.unshift(result.document)
      showUploadModal.value = false
      resetUploadForm()
    } else {
      console.error('Upload failed')
    }
  } catch (error) {
    console.error('Error uploading document:', error)
  } finally {
    isUploading.value = false
  }
}

const resetUploadForm = () => {
  selectedFile.value = null
  uploadForm.value = {
    title: '',
    category: '',
    description: '',
    accessLevel: 'all',
    expirationDate: ''
  }
}

const downloadDocument = async (document: any) => {
  try {
    const response = await fetch(`/api/company/documents/${document.id}/download`, {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = document.title
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      // Update download count
      document.downloads++
    }
  } catch (error) {
    console.error('Error downloading document:', error)
  }
}

const previewDocument = (document: any) => {
  // Open document in new tab for preview
  window.open(`/api/company/documents/${document.id}/preview`, '_blank')
}

const deleteDocument = async (documentId: number) => {
  if (!confirm('Are you sure you want to delete this document?')) return
  
  try {
    const response = await fetch(`/api/company/documents/${documentId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      documents.value = documents.value.filter(doc => doc.id !== documentId)
    }
  } catch (error) {
    console.error('Error deleting document:', error)
  }
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const navigateToSection = (route: string) => {
  console.log('Navigating to:', route)
}

const viewArticle = (article: any) => {
  console.log('Viewing article:', article)
}

const startTraining = (module: any) => {
  console.log('Starting training:', module)
}

const viewGuideline = (guideline: any) => {
  console.log('Viewing guideline:', guideline)
}

const formatDate = (date: string) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(date))
}

// Lifecycle
onMounted(() => {
  loadDocuments()
})
</script>

<style scoped>
/* Add any additional styles here */
</style>
